import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowRight,
  FiCheckCircle,
  FiFileText,
  FiAward,
} from "react-icons/fi";
import { settingsAPI } from "../services/api";

const steps = [
  {
    num: "01",
    title: "Online Application",
    desc: "Fill out the online admission form with student and parent information. Upload required documents.",
  },
  {
    num: "02",
    title: "Document Review",
    desc: "Our admissions team reviews your application and submitted documents within 3-5 business days.",
  },
  {
    num: "03",
    title: "Entrance Test",
    desc: "Qualified applicants are invited for a written entrance test based on the grade applied for.",
  },
  {
    num: "04",
    title: "Interview",
    desc: "Shortlisted students and parents are invited for an informal interview with the admissions panel.",
  },
  {
    num: "05",
    title: "Admission Offer",
    desc: "Successful applicants receive an admission offer letter with fee details and enrollment instructions.",
  },
  {
    num: "06",
    title: "Fee Payment & Enrollment",
    desc: "Pay the admission fee to confirm your seat and complete the enrollment process.",
  },
];

const documents = [
  "Birth Certificate (original + photocopy)",
  "Previous school's Transfer Certificate (TC)",
  "Previous year's Mark Sheet/Report Card",
  "Passport-size photographs (4 copies)",
  "Citizenship certificate of parents/guardians",
  "Character Certificate from previous school",
  "Medical fitness certificate",
  "Immunization records",
];

const fallbackFees = [
  {
    level: "Primary (Grade 1-5)",
    admission: "NPR 15,000",
    monthly: "NPR 3,500",
    annual: "NPR 8,000",
  },
  {
    level: "Secondary (Grade 6-10)",
    admission: "NPR 20,000",
    monthly: "NPR 4,500",
    annual: "NPR 10,000",
  },
  {
    level: "High School (Grade 11-12)",
    admission: "NPR 25,000",
    monthly: "NPR 5,500",
    annual: "NPR 12,000",
  },
];

const scholarships = [
  {
    title: "Merit Scholarship",
    desc: "Awarded to students scoring 90%+ in previous year's examination. Covers up to 50% of tuition fees.",
  },
  {
    title: "Need-Based Aid",
    desc: "Financial assistance for deserving students from economically disadvantaged backgrounds.",
  },
  {
    title: "Sports Excellence",
    desc: "For students representing school/district/national level in sports. Covers admission fee.",
  },
  {
    title: "Sibling Discount",
    desc: "10% discount on monthly tuition fees for siblings enrolled simultaneously.",
  },
];

const Admissions = () => {
  const [fees, setFees] = useState(fallbackFees);

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        const data = res.data?.data?.settings?.feeStructure || [];
        if (data.length > 0) setFees(data);
      })
      .catch(() => {});
  }, []);
  return (
    <div>
      {/* Hero */}
      <div className="hero-gradient py-24 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
              Join Our Family
            </p>
            <h1 className="text-5xl font-serif font-bold text-white mb-4">
              Admissions
            </h1>
            <p className="text-white/70 text-lg mb-8">
              We welcome students who are eager to learn, grow, and contribute
              to our vibrant school community. Admissions are open for the
              2025-2026 academic year.
            </p>
            <Link
              to="/admissions/apply"
              className="btn-primary inline-flex items-center gap-2"
            >
              Apply Online Now
              <FiArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Admission Process */}
      <section id="process" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">How to Apply</p>
            <h2 className="section-title">Admission Process</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 border border-gray-100 rounded-2xl p-6 relative"
              >
                <div className="text-6xl font-bold text-primary-50 absolute top-4 right-4 leading-none select-none">
                  {step.num}
                </div>
                <div className="relative">
                  <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center text-white font-bold text-sm mb-4">
                    {step.num}
                  </div>
                  <h3 className="font-bold text-primary-500 text-lg mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="section-subtitle mb-2">Be Prepared</p>
            <h2 className="section-title">Required Documents</h2>
          </motion.div>

          <div className="bg-white border border-gray-100 rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <FiFileText className="w-6 h-6 text-primary-500" />
              <h3 className="font-bold text-primary-500 text-lg">
                Document Checklist
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {documents.map((doc, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.06 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{doc}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-6 text-gray-500 text-xs">
              Note: All documents must be submitted in original with one set of
              photocopies. Documents not in English or Nepali must be
              accompanied by a certified translation.
            </p>
          </div>
        </div>
      </section>

      {/* Tuition Fees */}
      <section id="fees" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="section-subtitle mb-2">Transparent Pricing</p>
            <h2 className="section-title">Tuition & Fee Structure</h2>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
              <thead>
                <tr className="bg-primary-500 text-white">
                  <th className="text-left px-6 py-4 font-semibold text-sm">
                    Level
                  </th>
                  <th className="text-left px-6 py-4 font-semibold text-sm">
                    Admission Fee
                  </th>
                  <th className="text-left px-6 py-4 font-semibold text-sm">
                    Monthly Fee
                  </th>
                  <th className="text-left px-6 py-4 font-semibold text-sm">
                    Annual Fee
                  </th>
                </tr>
              </thead>
              <tbody>
                {fees.map((fee, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  >
                    <td className="px-6 py-4 font-medium text-primary-500">
                      {fee.level}
                    </td>
                    <td className="px-6 py-4 text-gray-700">{fee.admission}</td>
                    <td className="px-6 py-4 text-gray-700">{fee.monthly}</td>
                    <td className="px-6 py-4 text-gray-700">{fee.annual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-gray-500 text-xs text-center">
            * Fees are subject to revision. Additional charges may apply for
            transportation, books, and uniforms.
          </p>
        </div>
      </section>

      {/* Scholarships */}
      <section id="scholarships" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="section-subtitle mb-2">Financial Support</p>
            <h2 className="section-title">Scholarships & Aid</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {scholarships.map((s, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-100 rounded-2xl p-6 flex gap-4"
              >
                <div className="w-12 h-12 bg-secondary-50 border border-secondary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FiAward className="w-6 h-6 text-secondary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-primary-500 mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/admissions/apply"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-500 text-white rounded-xl font-bold hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-lg text-base"
            >
              Start Your Application
              <FiArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
